import { HatTextTooltip } from "@/components/tooltip";
import { noticeChipClassName } from "@/shared/ui/emptyState/public";
import type { QuantileRangeStripProps, QuantileSummary } from "./contract";
import styles from "./QuantileRangeStrip.module.css";

// 값을 min..max 구간 안의 0..1 위치로 바꾼다. 표본이 하나뿐이거나 모두 같은 값이면 폭이 0 이라
// 나눌 수 없다. 그때는 구간 전체를 채워 "퍼짐이 없다" 를 그대로 보여 준다.
function positionOf(value: number, summary: QuantileSummary): number {
  const span = summary.max - summary.min;
  return span === 0 ? 0 : (value - summary.min) / span;
}

function summaryText(summary: QuantileSummary, format: (value: number) => string): string {
  return [
    `최소 ${format(summary.min)}`,
    `1사분위 ${format(summary.p25)}`,
    `중앙값 ${format(summary.p50)}`,
    `3사분위 ${format(summary.p75)}`,
    `최대 ${format(summary.max)}`,
  ].join(", ");
}

// 5수 요약 한 줄. min–max 수염 위에 p25–p75 상자를 겹치고 중앙값을 표시한다.
//
// recharts 에는 상자그림이 없다. 이 형태는 차트 축 설정보다 좌표 계산이 대부분이라 DOM 으로 그린다.
// 차트 라이브러리를 교체해도 이 파일은 그대로 남는다.
export function QuantileRangeStrip({ title, summary, formatValue }: QuantileRangeStripProps) {
  if (summary === null) {
    return (
      <div className={styles.strip}>
        <span className={styles.title}>{title}</span>
        <span className={`${noticeChipClassName} ${styles.notice}`}>
          요약할 표본이 아직 없습니다.
        </span>
      </div>
    );
  }
  const boxStart = positionOf(summary.p25, summary);
  const boxEnd = positionOf(summary.p75, summary);
  const boxWidth = summary.max === summary.min ? 1 : boxEnd - boxStart;
  return (
    <div className={styles.strip}>
      <div className={styles.head}>
        <span className={styles.title}>{title}</span>
        <span className={styles.median}>중앙값 {formatValue(summary.p50)}</span>
      </div>
      <HatTextTooltip text={summaryText(summary, formatValue)}>
        <div
          className={styles.plot}
          role="img"
          aria-label={`${title}: ${summaryText(summary, formatValue)}`}
        >
          <div className={styles.whisker} />
          <div
            className={styles.box}
            style={{ left: `${boxStart * 100}%`, width: `${boxWidth * 100}%` }}
          />
          <div
            className={styles.medianMark}
            style={{ left: `${positionOf(summary.p50, summary) * 100}%` }}
          />
        </div>
      </HatTextTooltip>
      {/* 눈금은 구간의 양 끝뿐이다. 중앙값은 제목 줄에 적는다. 이 줄 가운데에 두면 마커가 왼쪽으로
          치우친 분포에서도 "가운데" 로 읽혀 분포를 반대로 전달한다. */}
      <div className={styles.scale}>
        <span>{formatValue(summary.min)}</span>
        <span>{formatValue(summary.max)}</span>
      </div>
    </div>
  );
}
